import { create } from 'zustand'
import { toast } from 'sonner'
import {
  notificationService,
  notificationTag,
  type NotificationEntityType,
  type NotificationItem,
  type NotificationSyncEvent,
} from '@/modules/notifications/services/notification.service'
import {
  closeAllShadeNotifications,
  closeShadeNotifications,
  closeShadeTags,
  shadeTags,
} from '@/modules/notifications/utils/shade-notifications'
import { normalizeApiError } from '@/services/api/errors'

const INBOX_PAGE_SIZE = 20
const MAX_ITEMS = 500
/** Undo window after a delete — the server delete waits this long. */
export const NOTIFICATION_UNDO_MS = 4000
const UNDO_TOAST_ID = 'notification-undo'

interface NotificationState {
  items: NotificationItem[]
  unreadCount: number
  isLoading: boolean
  isLoadingMore: boolean
  hasLoadedOnce: boolean
  hasMore: boolean
  page: number
  /** First page — initial inbox hydration. */
  fetchInbox: () => Promise<void>
  /** Next page (Notifications page "Load more"). */
  loadMore: () => Promise<void>
  /** Unread-only backfill — used after a WebSocket reconnect to catch missed events. */
  backfillUnread: () => Promise<void>
  /** Merge a notification pushed live over WebSocket. */
  receiveLive: (notification: NotificationItem) => void
  markRead: (id: string) => Promise<void>
  markUnread: (id: string) => Promise<void>
  toggleRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  /** Marks every unread item for one record as read — used when a task/lead
   * view loads, so the bell count drops without the user opening it. */
  markReadForEntity: (entityType: NotificationEntityType, entityId: string) => Promise<void>
  /** Hides one now; the server delete runs after the Undo window. */
  remove: (id: string) => void
  undoRemove: (id: string) => void
  /** Sends every delete still waiting out its Undo window (app going to background). */
  flushPendingDeletes: () => void
  clearAll: () => Promise<void>
  /** Another device/tab read, deleted or cleared (`notification.sync`). */
  applySync: (event: NotificationSyncEvent) => void
  /** Close phone banners for anything that is no longer unread (app open / resume). */
  syncShade: () => Promise<void>
  reset: () => void
}

/** Deletes waiting out the Undo window: id → hidden item + timer. */
const pendingDeletes = new Map<string, { item: NotificationItem; timer: ReturnType<typeof setTimeout> }>()

const pendingUnread = () => [...pendingDeletes.values()].filter((entry) => !entry.item.readAt).length

/** Server counts still include our pending (not yet sent) deletes. */
const localUnread = (serverCount: number) => Math.max(0, serverCount - pendingUnread())

function mergeById(existing: NotificationItem[], incoming: NotificationItem[]): NotificationItem[] {
  const byId = new Map(existing.map((item) => [item.id, item]))
  for (const item of incoming) if (!pendingDeletes.has(item.id)) byId.set(item.id, item)
  return Array.from(byId.values())
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, MAX_ITEMS)
}

/** Close a record's phone banner once nothing unread about it is left. */
function clearShadeFor(items: NotificationItem[], changed: NotificationItem[]) {
  const stillUnread = new Set(items.filter((item) => !item.readAt).map(notificationTag))
  void closeShadeTags(changed.map(notificationTag).filter((tag) => !stillUnread.has(tag)))
}

function dropPending(id: string) {
  const entry = pendingDeletes.get(id)
  if (!entry) return null
  clearTimeout(entry.timer)
  pendingDeletes.delete(id)
  return entry
}

/** Real backend-backed notification inbox (list, unread badge, live WebSocket merges). */
export const useNotificationStore = create<NotificationState>((set, get) => {
  const commitDelete = async (id: string) => {
    const entry = dropPending(id)
    if (!entry) return
    try {
      const result = await notificationService.delete(id)
      set({ unreadCount: localUnread(result.unreadCount) })
    } catch (err) {
      // Already gone (another device) is fine; anything else → put it back.
      if (normalizeApiError(err).status === 404) return
      set((state) => ({
        items: mergeById(state.items, [entry.item]),
        unreadCount: entry.item.readAt ? state.unreadCount : state.unreadCount + 1,
      }))
      toast.error("Couldn't delete. Try again.")
    }
  }

  return {
    items: [],
    unreadCount: 0,
    isLoading: false,
    isLoadingMore: false,
    hasLoadedOnce: false,
    hasMore: false,
    page: 1,

    fetchInbox: async () => {
      set({ isLoading: true })
      try {
        const result = await notificationService.list({ page: 1, pageSize: INBOX_PAGE_SIZE })
        set({
          items: mergeById([], result.items),
          unreadCount: localUnread(result.unreadCount),
          hasMore: result.hasNextPage,
          page: 1,
          isLoading: false,
          hasLoadedOnce: true,
        })
      } catch {
        set({ isLoading: false })
      }
    },

    loadMore: async () => {
      const { hasMore, isLoadingMore, page } = get()
      if (!hasMore || isLoadingMore) return
      set({ isLoadingMore: true })
      try {
        const result = await notificationService.list({ page: page + 1, pageSize: INBOX_PAGE_SIZE })
        set((state) => ({
          items: mergeById(state.items, result.items),
          unreadCount: localUnread(result.unreadCount),
          hasMore: result.hasNextPage,
          page: page + 1,
          isLoadingMore: false,
        }))
      } catch {
        set({ isLoadingMore: false })
        toast.error("Couldn't load more. Try again.")
      }
    },

    backfillUnread: async () => {
      try {
        const result = await notificationService.list({
          unread: true,
          page: 1,
          pageSize: INBOX_PAGE_SIZE,
        })
        set((state) => ({
          items: mergeById(state.items, result.items),
          unreadCount: localUnread(result.unreadCount),
          hasLoadedOnce: true,
        }))
      } catch {
        // keep existing state — next event or backfill reconciles it
      }
    },

    receiveLive: (notification) => {
      set((state) => {
        if (pendingDeletes.has(notification.id)) return state
        if (state.items.some((item) => item.id === notification.id)) return state
        return {
          items: [notification, ...state.items].slice(0, MAX_ITEMS),
          unreadCount: notification.readAt ? state.unreadCount : state.unreadCount + 1,
        }
      })
    },

    markRead: async (id) => {
      const beforeItems = get().items
      const beforeUnread = get().unreadCount
      const target = beforeItems.find((item) => item.id === id)
      if (!target || target.readAt) return

      const readAt = new Date().toISOString()
      const nextItems = beforeItems.map((item) => (item.id === id ? { ...item, readAt } : item))
      set({ items: nextItems, unreadCount: Math.max(0, beforeUnread - 1) })
      clearShadeFor(nextItems, [target])

      try {
        await notificationService.markRead(id)
      } catch {
        set({ items: beforeItems, unreadCount: beforeUnread })
      }
    },

    markUnread: async (id) => {
      const beforeItems = get().items
      const beforeUnread = get().unreadCount
      const target = beforeItems.find((item) => item.id === id)
      if (!target || !target.readAt) return

      set({
        items: beforeItems.map((item) => (item.id === id ? { ...item, readAt: null } : item)),
        unreadCount: beforeUnread + 1,
      })

      try {
        const result = await notificationService.markUnread(id)
        set({ unreadCount: localUnread(result.unreadCount) })
      } catch {
        set({ items: beforeItems, unreadCount: beforeUnread })
        toast.error("Couldn't update. Try again.")
      }
    },

    toggleRead: async (id) => {
      const target = get().items.find((item) => item.id === id)
      if (!target) return
      await (target.readAt ? get().markUnread(id) : get().markRead(id))
    },

    markAllRead: async () => {
      const beforeItems = get().items
      const beforeUnread = get().unreadCount
      if (beforeUnread === 0) return

      const readAt = new Date().toISOString()
      set({
        items: beforeItems.map((item) => (item.readAt ? item : { ...item, readAt })),
        unreadCount: 0,
      })
      void closeAllShadeNotifications()

      try {
        await notificationService.markAllRead()
      } catch {
        set({ items: beforeItems, unreadCount: beforeUnread })
      }
    },

    markReadForEntity: async (entityType, entityId) => {
      const beforeItems = get().items
      const beforeUnread = get().unreadCount
      const isUnreadMatch = (item: NotificationItem) =>
        !item.readAt && item.entityType === entityType && item.entityId === entityId
      const matches = beforeItems.filter(isUnreadMatch)
      // Banner can exist even when the item isn't loaded (older page) — clear the record's tag.
      void closeShadeTags([`${entityType}:${entityId}`])
      if (matches.length === 0) return

      const readAt = new Date().toISOString()
      set({
        items: beforeItems.map((item) => (isUnreadMatch(item) ? { ...item, readAt } : item)),
        unreadCount: Math.max(0, beforeUnread - matches.length),
      })

      try {
        const result = await notificationService.markReadForEntity(entityType, entityId)
        set({ unreadCount: localUnread(result.unreadCount) })
      } catch {
        set({ items: beforeItems, unreadCount: beforeUnread })
      }
    },

    remove: (id) => {
      const { items, unreadCount } = get()
      const target = items.find((item) => item.id === id)
      if (!target) return

      const nextItems = items.filter((item) => item.id !== id)
      set({
        items: nextItems,
        unreadCount: target.readAt ? unreadCount : Math.max(0, unreadCount - 1),
      })
      clearShadeFor(nextItems, [target])

      pendingDeletes.set(id, {
        item: target,
        timer: setTimeout(() => void commitDelete(id), NOTIFICATION_UNDO_MS),
      })
      toast('Notification deleted', {
        id: UNDO_TOAST_ID,
        duration: NOTIFICATION_UNDO_MS,
        action: { label: 'Undo', onClick: () => get().undoRemove(id) },
      })
    },

    undoRemove: (id) => {
      const entry = dropPending(id)
      if (!entry) return
      set((state) => ({
        items: mergeById(state.items, [entry.item]),
        unreadCount: entry.item.readAt ? state.unreadCount : state.unreadCount + 1,
      }))
    },

    flushPendingDeletes: () => {
      for (const id of [...pendingDeletes.keys()]) void commitDelete(id)
    },

    clearAll: async () => {
      const beforeItems = get().items
      const beforeUnread = get().unreadCount
      const beforeHasMore = get().hasMore
      // The server clears these too — no separate deletes needed.
      for (const id of [...pendingDeletes.keys()]) dropPending(id)
      toast.dismiss(UNDO_TOAST_ID)

      set({ items: [], unreadCount: 0, hasMore: false, page: 1 })
      void closeAllShadeNotifications()

      try {
        await notificationService.deleteAll()
      } catch {
        set({ items: beforeItems, unreadCount: beforeUnread, hasMore: beforeHasMore })
        toast.error("Couldn't clear. Try again.")
      }
    },

    applySync: (event) => {
      const { items } = get()
      const ids = new Set(event.ids ?? [])
      const readAt = event.readAt ?? new Date().toISOString()
      let nextItems = items
      let changed: NotificationItem[] = []

      switch (event.action) {
        case 'read':
        case 'read_entity': {
          const hit = (item: NotificationItem) =>
            event.action === 'read'
              ? ids.has(item.id)
              : item.entityType === event.entityType && item.entityId === event.entityId
          changed = items.filter((item) => hit(item) && !item.readAt)
          nextItems = items.map((item) => (hit(item) && !item.readAt ? { ...item, readAt } : item))
          if (event.action === 'read_entity') void closeShadeTags([`${event.entityType}:${event.entityId}`])
          break
        }
        case 'unread':
          nextItems = items.map((item) => (ids.has(item.id) ? { ...item, readAt: null } : item))
          break
        case 'read_all':
          nextItems = items.map((item) => (item.readAt ? item : { ...item, readAt }))
          void closeAllShadeNotifications()
          break
        case 'deleted':
          for (const id of ids) dropPending(id)
          changed = items.filter((item) => ids.has(item.id))
          nextItems = items.filter((item) => !ids.has(item.id))
          break
        case 'cleared':
          for (const id of [...pendingDeletes.keys()]) dropPending(id)
          nextItems = []
          void closeAllShadeNotifications()
          set({ hasMore: false, page: 1 })
          break
      }

      set({ items: nextItems, unreadCount: localUnread(event.unreadCount) })
      if (changed.length) clearShadeFor(nextItems, changed)
    },

    syncShade: async () => {
      if ((await shadeTags()).length === 0) return
      try {
        const result = await notificationService.list({ unread: true, page: 1, pageSize: 100 })
        const unreadTags = new Set(
          result.items.filter((item) => !pendingDeletes.has(item.id)).map(notificationTag),
        )
        await closeShadeNotifications((tag) => !unreadTags.has(tag))
        set({ unreadCount: localUnread(result.unreadCount) })
      } catch {
        // offline — next open / resume retries
      }
    },

    reset: () => {
      get().flushPendingDeletes()
      set({
        items: [],
        unreadCount: 0,
        isLoading: false,
        isLoadingMore: false,
        hasLoadedOnce: false,
        hasMore: false,
        page: 1,
      })
    },
  }
})
